import { CHANGE_KIND } from '@/lib/copy';
import { formatDelta } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { FileEntry, RepoGroup } from './rows';

/* ---------------------------------------------------------------------------
   THE CROSS-REPO FILE TREE.

   A multi-repo change is the thing this dashboard exists to review, and a flat
   list of paths from three repositories is unreadable: `src/config.ts` means a
   different file in each of them. So the tree has repos as its roots — in
   PROMOTION ORDER, the order the merges will run in — and collapses
   single-child directory chains the way a code host does, because `src` →
   `features` → `diff` as three rows of indentation is three rows that carry one
   fact.

   The change kind is a LETTER, not a colour: A, M, D, R, C, T. A reviewer scans
   a tree for deletions, and a colour-only marker fails exactly the reader most
   likely to be scanning it quickly.
--------------------------------------------------------------------------- */

interface DirNode {
  name: string;
  path: string;
  dirs: Map<string, DirNode>;
  files: FileEntry[];
}

function emptyDir(name: string, path: string): DirNode {
  return { name, path, dirs: new Map(), files: [] };
}

function buildTree(files: readonly FileEntry[]): DirNode {
  const root = emptyDir('', '');
  for (const file of files) {
    const segments = file.path.split('/').slice(0, -1);
    let node = root;
    let prefix = '';
    for (const segment of segments) {
      prefix = prefix ? `${prefix}/${segment}` : segment;
      let next = node.dirs.get(segment);
      if (!next) {
        next = emptyDir(segment, prefix);
        node.dirs.set(segment, next);
      }
      node = next;
    }
    node.files.push(file);
  }
  return root;
}

/** Collapse `a` → `b` → `c` into one `a/b/c` row when nothing branches. */
function collapse(node: DirNode): { label: string; node: DirNode } {
  let current = node;
  let label = node.name;
  while (current.files.length === 0 && current.dirs.size === 1) {
    const only = current.dirs.values().next().value as DirNode;
    label = `${label}/${only.name}`;
    current = only;
  }
  return { label, node: current };
}

export function FileTree({
  repos,
  selected,
  onSelect,
  filter,
  collapsedRepos,
  onToggleRepo,
}: {
  repos: readonly RepoGroup[];
  /** The file key the reviewer is focused on, or null for the whole diff. */
  selected: string | null;
  onSelect: (key: string | null) => void;
  filter: string;
  collapsedRepos: ReadonlySet<number>;
  onToggleRepo: (repoId: number) => void;
}) {
  const needle = filter.trim().toLowerCase();

  return (
    <nav aria-label="Changed files" className="min-w-0">
      <ul className="m-0 list-none p-0">
        {repos.map((repo) => {
          const files = needle
            ? repo.files.filter((f) => f.path.toLowerCase().includes(needle))
            : repo.files;
          const collapsed = collapsedRepos.has(repo.repoId);
          const tree = buildTree(files);

          return (
            <li key={repo.repoId} className="border-b border-rule last:border-b-0">
              <button
                type="button"
                onClick={() => onToggleRepo(repo.repoId)}
                aria-expanded={!collapsed}
                className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left transition-colors hover:bg-sunken"
              >
                <Chevron open={!collapsed} />
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink">{repo.slug}</span>
                <span className="shrink-0 font-mono text-tiny tabular-nums text-ink-3">#{repo.position}</span>
                {repo.changed ? (
                  <span className="shrink-0 font-mono text-tiny tabular-nums text-ink-3">
                    {formatDelta(repo.additions, repo.deletions)}
                  </span>
                ) : (
                  <span className="shrink-0 font-mono text-tiny text-ink-3">unchanged</span>
                )}
              </button>

              {collapsed ? null : files.length === 0 ? (
                <p className="px-2 pb-1.5 pl-6 font-mono text-tiny text-ink-3">
                  {needle ? 'no match in this repo' : 'no changed files'}
                </p>
              ) : (
                <div className="pb-1.5">
                  <DirRows node={tree} depth={0} selected={selected} onSelect={onSelect} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function DirRows({
  node,
  depth,
  selected,
  onSelect,
}: {
  node: DirNode;
  depth: number;
  selected: string | null;
  onSelect: (key: string | null) => void;
}) {
  const dirs = Array.from(node.dirs.values()).sort((a, b) => a.name.localeCompare(b.name));
  const files = [...node.files].sort((a, b) => a.path.localeCompare(b.path));

  return (
    <ul className="m-0 list-none p-0">
      {dirs.map((dir) => {
        const { label, node: target } = collapse(dir);
        return (
          <li key={dir.path}>
            <p
              className="truncate font-mono text-tiny text-ink-3"
              style={{ paddingLeft: `${14 + depth * 10}px` }}
              title={target.path}
            >
              {label}/
            </p>
            <DirRows node={target} depth={depth + 1} selected={selected} onSelect={onSelect} />
          </li>
        );
      })}

      {files.map((file) => {
        const kind = CHANGE_KIND[file.changeKind];
        const active = selected === file.key;
        const leaf = file.path.slice(file.path.lastIndexOf('/') + 1);
        return (
          <li key={file.key}>
            <button
              type="button"
              onClick={() => onSelect(active ? null : file.key)}
              title={`${kind.label} · ${file.path}`}
              style={{ paddingLeft: `${14 + depth * 10}px` }}
              className={cn(
                'flex w-full items-center gap-1.5 py-[3px] pr-2 text-left transition-colors',
                active ? 'bg-action-soft' : 'hover:bg-sunken',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'w-[9px] shrink-0 text-center font-mono text-tiny font-semibold',
                  kind.tone === 'ok' && 'text-ok',
                  kind.tone === 'danger' && 'text-danger',
                  kind.tone === 'warn' && 'text-warn',
                  kind.tone === 'neutral' && 'text-ink-3',
                )}
              >
                {kind.short}
              </span>
              <span className={cn('min-w-0 flex-1 truncate font-mono text-tiny', active ? 'text-ink' : 'text-ink-2')}>
                {leaf}
              </span>
              {file.isBinary ? (
                <span className="shrink-0 font-mono text-tiny text-ink-3">bin</span>
              ) : (
                <span className="shrink-0 font-mono text-tiny tabular-nums text-ink-3">
                  {formatDelta(file.additions, file.deletions)}
                </span>
              )}
              {file.truncated ? (
                <span className="shrink-0 font-mono text-tiny text-warn" title="Truncated at the per-file budget">
                  cut
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 8 8"
      className={cn('h-2 w-2 shrink-0 stroke-current text-ink-3 transition-transform', open && 'rotate-90')}
      fill="none"
      strokeWidth="1.5"
    >
      <path d="M2.5 1 6 4l-3.5 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
